import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { formatoMiles, desformatearMiles } from '../utils/formatUtils';

export default function AdminEditRegistroForm({ initialData, onSave, onClose }) {
  const [fecha, setFecha] = useState('');
  const [guia, setGuia] = useState('');
  const [clienteId, setClienteId] = useState('');
  const [modeloMaquina, setModeloMaquina] = useState('');
  const [serieMaquina, setSerieMaquina] = useState('');
  const [modeloTonerId, setModeloTonerId] = useState('');
  const [color, setColor] = useState('Black');
  const [tipoTonerInstaladoId, setTipoTonerInstaladoId] = useState('');
  const [contadorActual, setContadorActual] = useState('');
  const [aprobadoPor, setAprobadoPor] = useState('');
  const [entregadoPor, setEntregadoPor] = useState('');
  const [fechaEntrega, setFechaEntrega] = useState('');
  const [observaciones, setObservaciones] = useState('');

  const [clientes, setClientes] = useState([]);
  const [toners, setToners] = useState([]);
  const [tiposToner, setTiposToner] = useState([]);
  const [personal, setPersonal] = useState([]);

  useEffect(() => {
    const cargarListas = async () => {
      const [{ data: cls }, { data: tns }, { data: tipos }, { data: per }] = await Promise.all([
        supabase.from('clientes').select('id, nombre').order('nombre', { ascending: true }),
        supabase.from('toners').select('id, modelo').order('modelo', { ascending: true }),
        supabase.from('tipos_toner').select('id, nombre').order('nombre', { ascending: true }),
        supabase.from('personal_empresa').select('id, nombre').order('nombre', { ascending: true }),
      ]);
      setClientes(cls || []);
      setToners(tns || []);
      setTiposToner(tipos || []);
      setPersonal(per || []);
    };
    cargarListas();
  }, []);

  useEffect(() => {
    if (!initialData) return;

    // Cargar la fecha original sin modificarla
    if (initialData.fecha) {
      // Si la fecha viene con formato ISO, extraer solo la parte de fecha
      const fechaOriginal = initialData.fecha.split('T')[0];
      setFecha(fechaOriginal);
    } else {
      setFecha('');
    }

    setGuia(initialData.guia || '');
    setClienteId(initialData.cliente_id ? String(initialData.cliente_id) : '');
    setModeloMaquina(initialData.modelo_maquina || '');
    setSerieMaquina(initialData.serie_maquina || '');
    setModeloTonerId(initialData.modelo_toner_id ? String(initialData.modelo_toner_id) : '');
    setColor(initialData.color || 'Black');
    setTipoTonerInstaladoId(
      initialData.tipo_toner_instalado_id
        ? String(initialData.tipo_toner_instalado_id)
        : (initialData.tipos_toner_instalado?.id ? String(initialData.tipos_toner_instalado.id) : '')
    );
    setContadorActual(initialData.contador_actual != null ? String(initialData.contador_actual) : '');
    setAprobadoPor(initialData.aprobado_por ? String(initialData.aprobado_por) : '');
    setEntregadoPor(initialData.entregado_por ? String(initialData.entregado_por) : '');
    setFechaEntrega('');
    setObservaciones(initialData.observaciones || '');
  }, [initialData]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const toDbId = (v) => {
      if (v === '' || v === undefined || v === null) return null;
      return /^\d+$/.test(v) ? parseInt(v, 10) : v;
    };
    const pick = (v, fb) => (v === '' || v === undefined) ? fb : v;

    const payload = {
      id: initialData.id,
      fecha: fecha, // Usar la fecha tal cual, sin transformaciones
      guia: pick(guia, initialData.guia) || null,
      cliente_id: toDbId(pick(clienteId, initialData.cliente_id)),
      modelo_maquina: pick(modeloMaquina, initialData.modelo_maquina) || null,
      serie_maquina: pick(serieMaquina, initialData.serie_maquina) || null,
      modelo_toner_id: toDbId(pick(modeloTonerId, initialData.modelo_toner_id)),
      tipo_toner_instalado_id: toDbId(pick(tipoTonerInstaladoId, initialData.tipo_toner_instalado_id)),
      color: pick(color, initialData.color) || null,
      contador_actual: contadorActual !== '' ? parseInt(contadorActual, 10) : (initialData.contador_actual ?? null),
      aprobado_por: toDbId(pick(aprobadoPor, initialData.aprobado_por)) || null,
      entregado_por: toDbId(pick(entregadoPor, initialData.entregado_por)),
      observaciones: pick(observaciones, initialData.observaciones) || null,
    };

    onSave(payload);
  };

  return (
    <div className="bg-white rounded-2xl overflow-hidden">
      {/* Header con gradiente */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white bg-opacity-20 rounded-lg">
            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Editar Registro de Toner</h2>
            <p className="text-white/80 text-sm">Modifica los datos del registro seleccionado</p>
          </div>
        </div>
      </div>

      {/* Formulario */}
      <form onSubmit={handleSubmit} className="p-6">
        {/* Secciones organizadas */}

        {/* Informacion basica */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4 flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Informacion del Registro
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Fecha del Registro</label>
              <input
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all duration-200 bg-white"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Guia de Despacho</label>
              <input
                type="text"
                value={guia}
                onChange={(e) => setGuia(e.target.value)}
                placeholder="Numero de guia"
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all duration-200"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Cliente</label>
              <select
                value={clienteId}
                onChange={(e) => setClienteId(e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all duration-200 bg-white"
              >
                <option value="">-- Seleccionar cliente --</option>
                {clientes.map(c => (
                  <option key={c.id} value={String(c.id)}>{c.nombre}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Informacion de la maquina */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4 flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            Informacion de la Maquina
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Modelo de Maquina</label>
              <input
                type="text"
                value={modeloMaquina}
                onChange={(e) => setModeloMaquina(e.target.value)}
                placeholder="Ej: iR-ADV DX C357"
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all duration-200"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Numero de Serie</label>
              <input
                type="text"
                value={serieMaquina}
                onChange={(e) => setSerieMaquina(e.target.value)}
                placeholder="Ej: 2UM00524"
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all duration-200 font-mono"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Contador Actual</label>
              <input
                type="text"
                value={formatoMiles(contadorActual)}
                onChange={(e) => setContadorActual(desformatearMiles(e.target.value))}
                placeholder="Ej: 123.619"
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all duration-200 font-mono"
              />
            </div>
          </div>
        </div>

        {/* Informacion del toner */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4 flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
            </svg>
            Informacion del Toner
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Modelo de Toner</label>
              <select
                value={modeloTonerId}
                onChange={(e) => setModeloTonerId(e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all duration-200 bg-white"
              >
                <option value="">-- Seleccionar toner --</option>
                {toners.map(t => (
                  <option key={t.id} value={String(t.id)}>{t.modelo}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Color del Toner</label>
              <select
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all duration-200 bg-white"
              >
                <option value="Black">Black</option>
                <option value="Cyan">Cyan</option>
                <option value="Magenta">Magenta</option>
                <option value="Yellow">Yellow</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Tipo de Toner Despachado</label>
              <select
                value={tipoTonerInstaladoId}
                onChange={(e) => setTipoTonerInstaladoId(e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all duration-200 bg-white"
              >
                <option value="">-- Seleccionar tipo --</option>
                {tiposToner.map(t => (
                  <option key={t.id} value={String(t.id)}>{t.nombre}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Informacion de entrega */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4 flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            Informacion de Entrega
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Entregado por</label>
              <select
                value={entregadoPor}
                onChange={(e) => setEntregadoPor(e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all duration-200 bg-white"
              >
                <option value="">-- Seleccionar persona --</option>
                {personal.map(p => (
                  <option key={p.id} value={String(p.id)}>{p.nombre}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Sera Despachado (solo referencia)</label>
              <input
                type="date"
                value={fechaEntrega}
                onChange={(e) => setFechaEntrega(e.target.value)}
                className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all duration-200 bg-white"
              />
            </div>
          </div>
        </div>

        {/* Observaciones */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4 flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
            </svg>
            Observaciones
          </h3>
          <textarea
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all duration-200 resize-y min-h-[100px]"
            placeholder="Agregar observaciones adicionales..."
            rows={3}
          />
          <p className="text-xs text-gray-500 mt-1">Las observaciones pueden ser editadas por el administrador</p>
        </div>

        {/* Botones de accion */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
          <button
            type="submit"
            className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Guardar Cambios
          </button>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-all duration-200 border-2 border-gray-200"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Cancelar
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
